import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon24Billing } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import {
  AppDialogSection,
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { color, EL_COLORS } from '@/colors';
import { CreditsInput } from '@/UI/Input/CreditsInputs';
import { Input } from '@/UI/Input/Input';
import { User } from '@/types/auth.types';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { FPL_ICON_COLOR_VAR } from '@/UI/Icons/fplIcon';
import { formatNumberToShortString, formatNumberWithCommas } from '@/utils/numbers';
import { GetMoreCreditsRequest } from '@/components/SubscriptionsAndPayments/payments.types';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import {
  CREDITS_TO_IMAGES_RATIO,
  CREDITS_TO_VIDEO_SECONDS_RATIO,
  MAX_CREDIT_PRICE,
  MIN_CREDIT_PRICE,
  estimateGenerations,
} from './creditPurchase';

/**
 * Dialog width in px, matching the Figma frame. FPL's `sm`/`md` width presets are 240px/320px, so
 * neither lands on the design; `AppDialog` forwards a numeric width on both the FPL and MUI paths.
 */
const DIALOG_WIDTH_PX = 400;

const minPrice = MIN_CREDIT_PRICE;
const maxPrice = MAX_CREDIT_PRICE;
const defaultPrice = MIN_CREDIT_PRICE;

interface GetMoreCreditsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (creditsRequest: GetMoreCreditsRequest) => Promise<void>;
  isProcessing: boolean;
  hasError: boolean;
  setHasError: (hasError: boolean) => void;
  currentUser: User;
}

const openIntercom = () => {
  if (window.Intercom) {
    window.Intercom('show');
  }
};

/** Where Stripe sends the user back to once checkout completes. */
function buildCallbackUrl(credits: number): string {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('payment', 'credits');
  currentUrl.searchParams.set('amount', credits.toString());
  return currentUrl.toString();
}

function creditsPerDollarFor(currentUser: User): number {
  return currentUser?.activeWorkspace?.subscription?.creditsPerDollar || 100;
}

function CheckoutErrorBanner() {
  const { t } = useTranslation();

  return (
    <Flex sx={{ width: '100%', mb: 4, backgroundColor: color.Weavy_Error_08_T, borderRadius: 1, p: 2 }}>
      <TextSmRg color={token.textDanger}>
        <Trans
          i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE}
          components={{
            link: (
              <ButtonTextPlain onClick={openIntercom} color={token.textDanger} variant="body-sm-rg">
                {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE_LINK)}
              </ButtonTextPlain>
            ),
          }}
        />
      </TextSmRg>
    </Flex>
  );
}

/**
 * Shared amount handling for both branches: keep the typed dollar amount in state, clamp it to the
 * purchasable range, and flag the below-minimum case so the confirm button can block on it.
 */
function usePurchaseAmount(creditsPerDollar: number) {
  const [price, setPrice] = useState(defaultPrice);
  const [minPriceError, setMinPriceError] = useState(false);

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMinPriceError(false);
    const value = event.target.value.replace(/,/g, '');
    if (value === '') {
      setPrice(0);
      setMinPriceError(true);
      return;
    }
    let numericValue = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      if (numericValue > maxPrice) {
        numericValue = maxPrice;
      }
      if (numericValue < minPrice) {
        setMinPriceError(true);
      }
      setPrice(numericValue);
    }
  };

  const enforcePriceLimits = () => {
    setMinPriceError(false);
    if (price < minPrice) {
      setPrice(minPrice);
    } else if (price > maxPrice) {
      setPrice(maxPrice);
    }
  };

  return { price, minPriceError, handlePriceChange, enforcePriceLimits, credits: price * creditsPerDollar };
}

/**
 * Amount is typed as an exact dollar figure, with the credits it buys and what those credits are
 * worth in generations shown together on the line beneath the field.
 */
function FplGetMoreCreditsModal({
  open,
  onClose,
  onConfirm,
  isProcessing,
  hasError,
  currentUser,
}: GetMoreCreditsModalProps) {
  const { t } = useTranslation();
  const { price, minPriceError, handlePriceChange, enforcePriceLimits, credits } = usePurchaseAmount(
    creditsPerDollarFor(currentUser),
  );
  const { images, videoSeconds } = estimateGenerations(credits);

  return (
    <AppDialog open={open} onClose={onClose} width={DIALOG_WIDTH_PX}>
      <AppDialogSection>
        <AppDialogHeader component="div">
          <AppDialogTitle>{t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.TITLE)}</AppDialogTitle>
        </AppDialogHeader>
        <AppDialogContent>
          {hasError && <CheckoutErrorBanner />}
          <FlexCol sx={{ gap: cssVars.spacer.x1 }}>
            <Input
              label={
                <>
                  {t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.AMOUNT_LABEL)}
                  <span style={{ color: cssVars.color.textSecondary }}>*</span>
                </>
              }
              required
              size="large"
              value={price.toLocaleString()}
              autoFocus
              onChange={handlePriceChange}
              onBlur={enforcePriceLimits}
              // The design binds the currency symbol to the same `--color-text` as the amount, where
              // the shared adornment slot defaults to the softer icon/prefix colour.
              startAdornment={<span style={{ color: cssVars.color.text }}>$</span>}
              error={minPriceError}
              // The below-minimum message takes the place of the credits line rather than adding a
              // second one, so the modal keeps its height as the amount is edited.
              helperText={
                minPriceError ? t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.MIN_PRICE_ERROR, { minPrice }) : undefined
              }
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                autoComplete: 'off',
                min: minPrice,
                max: maxPrice,
              }}
            />
            {/*
              At the top of the range these numbers reach seven digits, which wrapped the line and
              pushed the modal taller. Short form (`1.0M`) keeps it to one line and matches how
              credit balances are already abbreviated elsewhere in the app.
            */}
            {!minPriceError && (
              <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <FlexCenVer>
                  {/*
                   * The design tints the glyph with `--color-icon-secondary`, a step softer than the
                   * `--color-icon` that FPL icons fall back to. This branch only renders under the
                   * migration flag, so the var is set unconditionally.
                   */}
                  <AsteriskIcon
                    width={24}
                    height={24}
                    style={{ [FPL_ICON_COLOR_VAR]: cssVars.color.iconSecondary } as CSSProperties}
                  />
                  <TextStdRg color={cssVars.color.textSecondary}>
                    {t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.CREDITS_AMOUNT, {
                      credits: formatNumberToShortString(credits),
                    })}
                  </TextStdRg>
                </FlexCenVer>
                <TextStdRg color={cssVars.color.textSecondary}>
                  {t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.GENERATIONS_ESTIMATE, {
                    images: formatNumberToShortString(images),
                    videoSeconds: formatNumberToShortString(videoSeconds),
                  })}
                </TextStdRg>
              </Flex>
            )}
          </FlexCol>
        </AppDialogContent>
        <AppDialogActions>
          <ButtonContained size="small" mode="outlined" onClick={onClose} disabled={isProcessing}>
            {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CANCEL)}
          </ButtonContained>
          <ButtonContained
            startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: color.Black92 }} /> : null}
            onClick={() =>
              void onConfirm({
                credits,
                payAmountInUsd: price,
                callbackUrl: buildCallbackUrl(credits),
              })
            }
            disabled={isProcessing || minPriceError}
            size="small"
          >
            {isProcessing
              ? t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PROCESSING)
              : t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.CTA_CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

function LegacyGetMoreCreditsModal({
  open,
  onClose,
  onConfirm,
  isProcessing,
  hasError,
  currentUser,
}: GetMoreCreditsModalProps) {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();
  const amountLabel = t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.AMOUNT_LABEL);

  const creditsPerDollar = creditsPerDollarFor(currentUser);

  const [price, setPrice] = useState(defaultPrice);
  const [credits, setCredits] = useState(defaultPrice * creditsPerDollar);
  const [minPriceError, setMinPriceError] = useState(false);
  const [estimatedImages, setEstimatedImages] = useState(defaultPrice * creditsPerDollar * CREDITS_TO_IMAGES_RATIO);
  const [estimatedVideos, setEstimatedVideos] = useState(
    defaultPrice * creditsPerDollar * CREDITS_TO_VIDEO_SECONDS_RATIO,
  );

  const callbackUrl = buildCallbackUrl(credits);

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMinPriceError(false);
    const value = event.target.value.replace(/,/g, '');
    if (value === '') {
      setPrice(0);
      setCredits(0);
      setEstimatedImages(0);
      setEstimatedVideos(0);
      setMinPriceError(true);
      return;
    }
    let numericValue = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      if (numericValue > maxPrice) {
        numericValue = maxPrice;
      }
      if (numericValue < minPrice) {
        setMinPriceError(true);
      }
      setPrice(numericValue);
      setCredits(numericValue * creditsPerDollar);
      setEstimatedImages(numericValue * creditsPerDollar * CREDITS_TO_IMAGES_RATIO);
      setEstimatedVideos(numericValue * creditsPerDollar * CREDITS_TO_VIDEO_SECONDS_RATIO);
    }
  };

  const enforcePriceLimits = () => {
    setMinPriceError(false);
    let adjustedPrice = price;
    if (price < minPrice) {
      adjustedPrice = minPrice;
    } else if (price > maxPrice) {
      adjustedPrice = maxPrice;
    }
    setPrice(adjustedPrice);
    setCredits(adjustedPrice * creditsPerDollar);
    setEstimatedImages(adjustedPrice * creditsPerDollar * CREDITS_TO_IMAGES_RATIO);
    setEstimatedVideos(adjustedPrice * creditsPerDollar * CREDITS_TO_VIDEO_SECONDS_RATIO);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 420, backgroundColor: color.Black92 } } }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5, pb: 0 }}>
          <AppDialogTitle>{t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.TITLE)}</AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} size={24} />
        </AppDialogHeader>
        <AppDialogContent sx={{ px: 3, pb: 3, '&&': { pt: 4 } }}>
          {hasError && <CheckoutErrorBanner />}
          <CreditsInput
            label={amountLabel}
            required
            value={price.toLocaleString()}
            autoFocus
            onBlur={enforcePriceLimits}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                enforcePriceLimits();
              }
            }}
            sx={{ width: '100%' }}
            onChange={handlePriceChange}
            startAdornment={isFpl ? <Icon24Billing /> : <span>$&nbsp;</span>}
            error={minPriceError}
            helperText={
              minPriceError
                ? t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.MIN_PRICE_ERROR, { minPrice })
                : t(I18N_KEYS.PAYMENTS.GET_MORE_CREDITS_MODAL.GENERATIONS_ESTIMATE, {
                    images: formatNumberWithCommas(Math.round(estimatedImages)),
                    videoSeconds: formatNumberWithCommas(Math.round(estimatedVideos)),
                  })
            }
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: 'off',
              min: minPrice,
              max: maxPrice,
            }}
          />
        </AppDialogContent>
        <AppDialogActions
          sx={{ justifyContent: 'flex-end', px: 0, mx: 3, py: 3, borderTop: `1px solid ${EL_COLORS.DividerBorder}` }}
        >
          <ButtonContained size="small" mode="outlined" onClick={onClose} disabled={isProcessing}>
            {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CANCEL)}
          </ButtonContained>
          <ButtonContained
            startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: color.Black92 }} /> : null}
            onClick={() =>
              void onConfirm({
                credits: credits,
                payAmountInUsd: price,
                callbackUrl: callbackUrl,
              })
            }
            disabled={isProcessing || minPriceError}
            size="small"
          >
            {isProcessing
              ? t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PROCESSING)
              : t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

function GetMoreCreditsModal(props: GetMoreCreditsModalProps) {
  const isFpl = useFplMigrationFlag();
  const { hasError, setHasError } = props;

  // `CreditsContext` only renders this while the modal is open, so mounting is opening: clear any
  // checkout error left over from a previous attempt.
  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  return isFpl ? <FplGetMoreCreditsModal {...props} /> : <LegacyGetMoreCreditsModal {...props} />;
}

export default GetMoreCreditsModal;
